/**
 * Recent Orders Utility
 *
 * Manages recently created orders in sessionStorage to provide immediate
 * visibility on the Order History page, working around Cloudflare KV's
 * eventual consistency for list() operations.
 */

import type { OrderHistoryEntry, ShipmentResult, Address, Rate, DateFilters } from '../types';
import {
  getEffectiveRefundStatus,
  getRefundUpdateTimestamp,
  shouldApplyLocalRefundOverride,
} from './refunds';
import { getOperatorScopedStorageKey } from './operatorSession';
import { getStoredApiUrl } from '../api/config';
import { canonicalizeRetryApiIdentity } from './retryApiIdentity';
import { matchesOrderStatus } from './orderFilters';

const STORAGE_KEY = 'easypost_recent_orders';
const MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes - orders should appear in KV by then

function storageKey(): string | null {
  const apiIdentity = canonicalizeRetryApiIdentity(getStoredApiUrl());
  // Unbound caches from older builds cannot safely be assigned to the current
  // API. Keep each endpoint's purchases and refund overrides separate.
  return getOperatorScopedStorageKey(`${STORAGE_KEY}:v2:${encodeURIComponent(apiIdentity)}`);
}

interface RecentOrder extends OrderHistoryEntry {
  _localTimestamp: number; // When this was added to sessionStorage
  _statusOverride?: boolean;
}

/**
 * Get all recent orders from sessionStorage
 */
export function getRecentOrders(): RecentOrder[] {
  try {
    const key = storageKey();
    if (!key) return [];
    const stored = sessionStorage.getItem(key);
    if (!stored) return [];

    const orders: RecentOrder[] = JSON.parse(stored);
    const now = Date.now();

    // Filter out orders older than MAX_AGE_MS
    return orders.filter(order => (now - order._localTimestamp) < MAX_AGE_MS);
  } catch {
    return [];
  }
}

/**
 * Save a recent order to sessionStorage
 */
export function saveRecentOrder(
  result: ShipmentResult,
  toAddress: Address,
  rate: Rate | null
): void {
  try {
    const existing = getRecentOrders();

    // Build the order history entry from the shipment result
    const destination = [
      toAddress.city,
      toAddress.state,
      toAddress.zip,
      toAddress.country && toAddress.country !== 'US' ? toAddress.country : '',
    ]
      .filter(Boolean)
      .join(', ');

    const newOrder: RecentOrder = {
      shipment_id: result.shipment_id,
      tracking_code: result.tracking_code,
      label_url: result.label_url,
      label_file_type: result.label_file_type,
      carrier: result.rate?.carrier || rate?.carrier || null,
      service: result.rate?.service || rate?.service || null,
      postage_amount: result.rate?.rate || rate?.rate || null,
      postage_currency: result.rate?.currency || rate?.currency || null,
      address_snapshot: {
        name: toAddress.name || '', company: toAddress.company || '',
        street1: toAddress.street1, street2: toAddress.street2 || '',
        city: toAddress.city, state: toAddress.state, zip: toAddress.zip,
        country: toAddress.country || 'US', email: toAddress.email || '', phone: toAddress.phone || '', signature: null,
      },
      recipient: toAddress.name || toAddress.company || 'Unknown',
      destination,
      created_at: new Date().toISOString(),
      status: 'purchased',
      _localTimestamp: Date.now(),
    };

    // Add to the beginning of the list
    const updated = [newOrder, ...existing.filter((order) => order.shipment_id !== newOrder.shipment_id)];

    const key = storageKey();
    if (!key) return;
    sessionStorage.setItem(key, JSON.stringify(updated));
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

/**
 * Remove orders that now appear in the API response
 */
export function pruneRecentOrders(apiOrders: OrderHistoryEntry[]): void {
  try {
    const key = storageKey();
    if (!key) return;
    const recent = getRecentOrders();
    if (recent.length === 0) return;

    // Create a Set of shipment_ids from the API response for O(1) lookup
    const apiOrdersByShipment = new Map(
      apiOrders.map((order) => [order.shipment_id, order])
    );

    // Keep only orders that haven't appeared in the API yet
    const remaining = recent.filter((order) => {
      const apiOrder = apiOrdersByShipment.get(order.shipment_id);
      if (!apiOrder) return true;
      if (!order._statusOverride) return false;

      return shouldApplyLocalRefundOverride(apiOrder, order);
    });

    if (remaining.length === 0) {
      sessionStorage.removeItem(key);
    } else if (remaining.length !== recent.length) {
      sessionStorage.setItem(key, JSON.stringify(remaining));
    }
  } catch {
    // Silently fail
  }
}

/**
 * Merge recent orders with API orders, avoiding duplicates.
 *
 * `rawApiOrders` is the un-overridden server data used for pruning decisions.
 * Callers that pass an already locally-patched list as `apiOrders` must supply
 * the raw list too, otherwise pruning compares a stored override against an
 * entry patched with its own timestamps and wrongly drops it.
 */
export function mergeWithRecentOrders(
  apiOrders: OrderHistoryEntry[],
  rawApiOrders: OrderHistoryEntry[] = apiOrders
): OrderHistoryEntry[] {
  const recent = getRecentOrders();
  if (recent.length === 0) return apiOrders;

  const apiShipmentIds = new Set(apiOrders.map(o => o.shipment_id).filter(Boolean));
  const statusOverrides = new Map(
    recent
      .filter((order) => order._statusOverride)
      .map((order) => [order.shipment_id, order])
  );

  const reconciledApiOrders = apiOrders.map((order) => {
    const override = statusOverrides.get(order.shipment_id);
    if (!override || !shouldApplyLocalRefundOverride(order, override)) return order;
    return {
      ...order,
      status: override.status,
      refund_status: override.refund_status,
      status_updated_at: override.status_updated_at,
      refund_updated_at: override.refund_updated_at,
      cancelled_at: override.cancelled_at,
    };
  });

  // Prune purchases once they appear in the API, but retain local status
  // overrides until the corresponding KV update is visible. Pruning must see
  // the raw server rows, not the locally-patched display rows.
  pruneRecentOrders(rawApiOrders);

  // Filter recent orders that haven't appeared in the API yet
  const newOrders = recent.filter(
    order => !apiShipmentIds.has(order.shipment_id)
  );

  if (newOrders.length === 0) return reconciledApiOrders;

  // Merge: recent orders first, then API orders
  // Remove the internal _localTimestamp property before returning
  const cleanedRecentOrders: OrderHistoryEntry[] = newOrders.map(
    ({ _localTimestamp, _statusOverride, ...order }) => order
  );

  return [...cleanedRecentOrders, ...reconciledApiOrders];
}

function localDateKey(dateValue: string): string {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function matchesFilters(order: OrderHistoryEntry, filters: DateFilters): boolean {
  if (!matchesOrderStatus(order, filters.status)) return false;
  const search = filters.search?.trim().toLowerCase();
  if (search) {
    const searchable = [order.recipient, order.destination, order.tracking_code]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!searchable.includes(search)) return false;
  }

  const orderDate = localDateKey(order.created_at);
  if (!orderDate) return false;

  if (filters.date && !filters.startDate && !filters.endDate && orderDate !== filters.date) {
    return false;
  }
  if (filters.startDate && orderDate < filters.startDate) return false;
  if (filters.endDate && orderDate > filters.endDate) return false;
  return true;
}

/**
 * Merge recent orders while respecting the same filters applied by the API.
 * `rawApiOrders` (the un-overridden server rows) is forwarded for pruning;
 * see mergeWithRecentOrders.
 */
export function mergeFilteredRecentOrders(
  apiOrders: OrderHistoryEntry[],
  filters: DateFilters,
  rawApiOrders: OrderHistoryEntry[] = apiOrders
): OrderHistoryEntry[] {
  const merged = mergeWithRecentOrders(apiOrders, rawApiOrders);
  const apiIds = new Set(apiOrders.map((order) => order.shipment_id));
  return merged.filter((order) => matchesOrderStatus(order, filters.status) && (apiIds.has(order.shipment_id) || matchesFilters(order, filters)));
}

/**
 * Keep the local consistency record aligned with cancellation/refund state.
 */
export function updateRecentOrderStatus(
  shipmentId: string,
  status: string,
  statusUpdatedAt = new Date().toISOString(),
  patch: Partial<OrderHistoryEntry> = {}
): void {
  try {
    const key = storageKey();
    if (!key) return;
    // Read the raw stored list (not the age-filtered view) so the override
    // can still attach to an entry that has aged past MAX_AGE_MS.
    const stored = sessionStorage.getItem(key);
    const recent: RecentOrder[] = stored ? JSON.parse(stored) : [];
    let applied = false;
    const updated = recent.map((order) => {
      if (order.shipment_id !== shipmentId) return order;
      applied = true;
      return {
        ...order,
        ...patch,
        status: patch.status || order.status,
        refund_status: status,
        status_updated_at: statusUpdatedAt,
        refund_updated_at: patch.refund_updated_at || statusUpdatedAt,
        _statusOverride: true,
        // Restart the retention window: the override must live a full
        // MAX_AGE_MS from when it was written, not from the original
        // purchase, or it can expire seconds after the cancellation.
        _localTimestamp: Date.now(),
      };
    });
    if (!applied) {
      // The purchase entry was already pruned (it appeared in the API) or was
      // never stored locally. Persist a status-only override so the
      // cancellation still survives KV's eventual consistency.
      updated.unshift({
        shipment_id: shipmentId,
        ...patch,
        status: patch.status || 'purchased',
        refund_status: status,
        status_updated_at: statusUpdatedAt,
        refund_updated_at: patch.refund_updated_at || statusUpdatedAt,
        _statusOverride: true,
        _localTimestamp: Date.now(),
      } as RecentOrder);
    }
    sessionStorage.setItem(key, JSON.stringify(updated));
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

export { getEffectiveRefundStatus, getRefundUpdateTimestamp };

/**
 * Clear all recent orders (useful for testing/debugging)
 */
export function clearRecentOrders(): void {
  try {
    const key = storageKey();
    if (key) sessionStorage.removeItem(key);
  } catch {
    // Silently fail
  }
}
